import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowDownLeft,
  ArrowUpRight,
  Banknote,
  Building2,
  CheckCircle,
  ChevronDown,
  Clock,
  IndianRupee,
  RefreshCw,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { TechnicianBankAccount, TechnicianWallet, WalletTransaction } from '../types';

// ── helpers ────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (s: string) =>
  new Date(s).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
  });

type OtpStep = 'idle' | 'requesting' | 'otp_sent' | 'confirming' | 'done';

// ── component ──────────────────────────────────────────────────────────────

const WalletPage: React.FC = () => {
  const { user } = useAuth();
  const technicianId = user?.technicianIds?.[0] || user?.id || 0;

  const [wallet, setWallet]   = useState<TechnicianWallet | null>(null);
  const [txns, setTxns]       = useState<WalletTransaction[]>([]);
  const [bank, setBank]       = useState<TechnicianBankAccount | null | undefined>(undefined); // undefined = loading
  const [loading, setLoading] = useState(true);
  const [txPage, setTxPage]   = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [txLoading, setTxLoading] = useState(false);

  // Withdrawal OTP flow
  const [otpStep, setOtpStep]     = useState<OtpStep>('idle');
  const [requestId, setRequestId] = useState<number | null>(null);
  const [otp, setOtp]             = useState('');
  const [withdrawMsg, setWithdrawMsg]   = useState('');
  const [withdrawError, setWithdrawError] = useState('');
  const otpInputRef = useRef<HTMLInputElement>(null);

  // ── data loading ──────────────────────────────────────────────────────

  const loadWallet = useCallback(async () => {
    if (!technicianId) return;
    try {
      const [w, b] = await Promise.all([
        apiService.getWallet(technicianId),
        apiService.getWalletBankAccount(technicianId),
      ]);
      setWallet(w);
      setBank(b);
    } catch {
      setBank(null);
    }
  }, [technicianId]);

  const loadTxns = useCallback(async (page: number) => {
    if (!technicianId) return;
    setTxLoading(true);
    try {
      const items = await apiService.getWalletTransactions(technicianId, page, 20);
      if (page === 1) {
        setTxns(items);
      } else {
        setTxns(prev => [...prev, ...items]);
      }
      setHasMore(items.length === 20);
    } catch {
      // silent — don't block the page
    } finally {
      setTxLoading(false);
    }
  }, [technicianId]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadWallet(), loadTxns(1)]);
      setLoading(false);
    };
    init();
  }, [loadWallet, loadTxns]);

  const handleLoadMore = () => {
    const next = txPage + 1;
    setTxPage(next);
    loadTxns(next);
  };

  // ── withdrawal flow ───────────────────────────────────────────────────

  const handleRequestOtp = async () => {
    setWithdrawError('');
    setWithdrawMsg('');
    setOtpStep('requesting');
    try {
      const res = await apiService.requestWithdrawalOtp(technicianId);
      // Extract requestId from message "... RequestId: 12. ..."
      const match = res.message?.match(/RequestId:\s*(\d+)/i);
      if (match) setRequestId(Number(match[1]));
      setWithdrawMsg(res.message || 'OTP sent to your WhatsApp number.');
      setOtpStep('otp_sent');
      setTimeout(() => otpInputRef.current?.focus(), 100);
    } catch (err: any) {
      setWithdrawError(err?.response?.data?.message || err?.message || 'Failed to send OTP.');
      setOtpStep('idle');
    }
  };

  const handleConfirmOtp = async () => {
    if (!otp || otp.length !== 6) {
      setWithdrawError('Enter the 6-digit OTP sent to your WhatsApp.');
      return;
    }
    if (!requestId) {
      setWithdrawError('Session expired. Please request a new OTP.');
      return;
    }
    setWithdrawError('');
    setOtpStep('confirming');
    try {
      const res = await apiService.confirmWithdrawal(technicianId, requestId, otp);
      setWithdrawMsg(res.message || 'Withdrawal processed successfully.');
      setOtpStep('done');
      setOtp('');
      setRequestId(null);
      // Refresh wallet balance
      await loadWallet();
      await loadTxns(1);
      setTxPage(1);
    } catch (err: any) {
      setWithdrawError(err?.response?.data?.message || err?.message || 'OTP verification failed.');
      setOtpStep('otp_sent');
    }
  };

  const resetWithdraw = () => {
    setOtpStep('idle');
    setOtp('');
    setRequestId(null);
    setWithdrawMsg('');
    setWithdrawError('');
  };

  // ── render ────────────────────────────────────────────────────────────

  if (loading) {
    return <div className="empty-state mobile-empty">Loading wallet...</div>;
  }

  const canWithdraw =
    wallet && wallet.availableBalance > 0 && bank && otpStep === 'idle';

  return (
    <div>
      <div className="page-heading">
        <div>
          <h1>My Wallet</h1>
          <p>Earnings, withdrawals and bank details</p>
        </div>
        <button
          className="refresh-button"
          onClick={async () => { await loadWallet(); await loadTxns(1); setTxPage(1); }}
        >
          <RefreshCw size={14} />
          <span>Refresh</span>
        </button>
      </div>

      {/* ── Balance card ── */}
      <div className="detail-hero-card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <Wallet size={18} color="#0284c7" />
          <span style={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>Wallet Balance</span>
        </div>

        <div style={{ fontSize: 36, fontWeight: 900, color: '#0f172a', marginBottom: 4 }}>
          {fmt(wallet?.availableBalance ?? 0)}
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>Available to withdraw</div>

        {(wallet?.pendingBalance ?? 0) > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: '#fef9c3', border: '1px solid #fde68a',
            borderRadius: 8, padding: '8px 12px', marginBottom: 16,
            fontSize: 12, fontWeight: 700, color: '#92400e',
          }}>
            <Clock size={13} />
            {fmt(wallet!.pendingBalance)} pending (T+2 settlement)
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="detail-section-card" style={{ padding: 12, margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#15803d' }}>
              <TrendingUp size={14} />
              <span style={{ fontSize: 11, fontWeight: 800 }}>Total Earned</span>
            </div>
            <strong style={{ fontSize: 16, color: '#166534' }}>{fmt(wallet?.totalEarned ?? 0)}</strong>
          </div>
          <div className="detail-section-card" style={{ padding: 12, margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#0369a1' }}>
              <Banknote size={14} />
              <span style={{ fontSize: 11, fontWeight: 800 }}>Total Withdrawn</span>
            </div>
            <strong style={{ fontSize: 16, color: '#075985' }}>{fmt(wallet?.totalWithdrawn ?? 0)}</strong>
          </div>
        </div>
      </div>

      {/* ── Bank account ── */}
      <div className="detail-section-card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Building2 size={16} color="#475569" />
          <h2 style={{ margin: 0, fontSize: 14 }}>Bank Account</h2>
        </div>

        {bank === undefined ? (
          <div style={{ fontSize: 12, color: '#94a3b8' }}>Loading...</div>
        ) : bank === null ? (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 12px' }}>
            <p style={{ margin: 0, fontSize: 12, color: '#991b1b', fontWeight: 700 }}>No bank account added</p>
            <p style={{ margin: '4px 0 0', fontSize: 11, color: '#b91c1c' }}>Contact admin to add your bank details before withdrawing.</p>
          </div>
        ) : (
          <div className="detail-grid">
            <div><span>Account Holder</span><strong>{bank.accountHolderName || '-'}</strong></div>
            <div><span>Bank</span><strong>{bank.bankName || '-'}</strong></div>
            <div><span>Account No.</span><strong>{bank.accountNumber || '-'}</strong></div>
            <div><span>IFSC</span><strong>{bank.ifsc || '-'}</strong></div>
          </div>
        )}
      </div>

      {/* ── Withdraw section ── */}
      <div className="detail-section-card" style={{ marginBottom: 12 }}>
        <h2 style={{ margin: '0 0 12px', fontSize: 14 }}>Withdraw Earnings</h2>

        {withdrawMsg && otpStep === 'done' && (
          <div className="payment-success-banner" style={{ marginBottom: 12 }}>
            <CheckCircle size={16} />
            {withdrawMsg}
          </div>
        )}

        {withdrawError && (
          <div className="error-message" style={{ marginBottom: 12 }}>{withdrawError}</div>
        )}

        {otpStep === 'idle' && (
          <>
            {!bank ? (
              <div style={{ fontSize: 12, color: '#94a3b8' }}>Add a bank account to withdraw.</div>
            ) : wallet && wallet.availableBalance <= 0 ? (
              <div style={{ fontSize: 12, color: '#94a3b8' }}>No balance available to withdraw.</div>
            ) : (
              <button
                className="primary-action"
                onClick={handleRequestOtp}
                disabled={!canWithdraw}
              >
                <IndianRupee size={16} />
                Withdraw {fmt(wallet?.availableBalance ?? 0)}
              </button>
            )}
          </>
        )}

        {otpStep === 'requesting' && (
          <button className="primary-action" disabled>
            <RefreshCw size={16} style={{ animation: 'spin 0.8s linear infinite' }} />
            Sending OTP...
          </button>
        )}

        {otpStep === 'otp_sent' && (
          <div style={{ display: 'grid', gap: 10 }}>
            {withdrawMsg && (
              <div className="status-message">{withdrawMsg}</div>
            )}
            <label style={{ fontSize: 12, fontWeight: 700, color: '#334155', display: 'grid', gap: 6 }}>
              Enter 6-digit OTP sent to your WhatsApp
              <input
                ref={otpInputRef}
                type="tel"
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={e => e.key === 'Enter' && handleConfirmOtp()}
                placeholder="• • • • • •"
                style={{
                  border: '1px solid #dbe4ef', borderRadius: 8,
                  padding: '10px 14px', fontSize: 22, letterSpacing: 10,
                  fontWeight: 900, textAlign: 'center',
                }}
              />
            </label>
            <button className="primary-action" onClick={handleConfirmOtp}>
              <CheckCircle size={16} />
              Confirm &amp; Withdraw
            </button>
            <button
              style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 12, padding: 4, cursor: 'pointer' }}
              onClick={resetWithdraw}
            >
              Cancel
            </button>
          </div>
        )}

        {otpStep === 'confirming' && (
          <button className="primary-action" disabled>
            <RefreshCw size={16} style={{ animation: 'spin 0.8s linear infinite' }} />
            Processing withdrawal...
          </button>
        )}

        {otpStep === 'done' && (
          <button className="primary-action" onClick={resetWithdraw} style={{ marginTop: 4 }}>
            Make Another Withdrawal
          </button>
        )}
      </div>

      {/* ── Transaction history ── */}
      <div className="detail-section-card" style={{ marginBottom: 80 }}>
        <h2 style={{ margin: '0 0 12px', fontSize: 14 }}>Transaction History</h2>

        {txns.length === 0 && !txLoading ? (
          <div className="empty-state compact-empty">No transactions yet.</div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {txns.map(tx => {
              const isCredit = tx.type?.toLowerCase() === 'credit';
              return (
                <div key={tx.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 12, padding: '10px 12px',
                  background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: 34, height: 34, borderRadius: 8,
                      background: isCredit ? '#ecfdf5' : '#e0f2fe',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      {isCredit
                        ? <ArrowDownLeft size={16} color="#16a34a" />
                        : <ArrowUpRight size={16} color="#0284c7" />}
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>
                        {tx.description || (isCredit ? 'Order Credit' : 'Withdrawal')}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                        {fmtDate(tx.createdAt)}
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{
                      fontSize: 14, fontWeight: 900,
                      color: isCredit ? '#16a34a' : '#0284c7',
                    }}>
                      {isCredit ? '+' : '-'}{fmt(tx.amount)}
                    </div>
                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2, textTransform: 'uppercase' }}>
                      {tx.status}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {txLoading && (
          <div style={{ textAlign: 'center', padding: 16, color: '#94a3b8', fontSize: 12 }}>
            Loading...
          </div>
        )}

        {hasMore && !txLoading && txns.length > 0 && (
          <button
            onClick={handleLoadMore}
            style={{
              width: '100%', marginTop: 10,
              background: 'none', border: '1px solid #e2e8f0',
              borderRadius: 8, padding: '10px', fontSize: 12,
              fontWeight: 700, color: '#475569', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}
          >
            <ChevronDown size={14} />
            Load more
          </button>
        )}
      </div>
    </div>
  );
};

export default WalletPage;
